import { memo } from 'react'
import { Handle, NodeProps, Position, useReactFlow } from 'reactflow'
import { CloseIcon } from 'assets/icons/CloseIcon'
import {
  CalcComponentNodeBox,
  FunctionNodeBox,
  FunctionNodeTitle,
  FunctionPort,
  FunctionPortLabel,
  FunctionPortGroupLabel,
  FunctionPortsRow,
  InputComponentNodeBox,
  TaskNodeDeleteBtn,
} from '../DragComponents/Tasks.styled'

export type InputNodeData = { name: string; component: number; componentId: number }
export type CalcNodeData = {
  name: string
  component: number
  componentId: number
  wired: boolean
}
export type FunctionPortShape = { name: string; component: number; order: number }
export type FunctionNodeData = {
  name: string
  fid: number
  inputs: FunctionPortShape[]
  outputs: FunctionPortShape[]
  connected: boolean
  onRemove?: (fid: number) => void
}

export const InputComponentNode = memo(({ data, selected }: NodeProps<InputNodeData>) => (
  <InputComponentNodeBox selected={selected}>
    {data.name}
    <Handle type='source' position={Position.Right} id='out' />
  </InputComponentNodeBox>
))
InputComponentNode.displayName = 'InputComponentNode'

export const CalcComponentNode = memo(({ data, selected }: NodeProps<CalcNodeData>) => (
  <CalcComponentNodeBox selected={selected} wired={data.wired}>
    <Handle type='target' position={Position.Left} id='in' />
    {data.name}
  </CalcComponentNodeBox>
))
CalcComponentNode.displayName = 'CalcComponentNode'

export const FunctionNode = memo(({ data, selected, id }: NodeProps<FunctionNodeData>) => {
  const { setNodes, setEdges } = useReactFlow()

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    setNodes((prev) => prev.filter((n) => n.id !== id))
    setEdges((prev) => prev.filter((edge) => edge.source !== id && edge.target !== id))
    data.onRemove?.(data.fid)
  }

  return (
    <FunctionNodeBox selected={selected} connected={data.connected}>
      <FunctionNodeTitle>{data.name}</FunctionNodeTitle>
      <TaskNodeDeleteBtn
        className='task-node-delete'
        onClick={handleDelete}
        title='Remove function'
      >
        <CloseIcon />
      </TaskNodeDeleteBtn>
      {data.inputs.length > 0 && (
        <>
          <FunctionPortGroupLabel>Inputs</FunctionPortGroupLabel>
          <FunctionPortsRow>
            {data.inputs.map((inp) => (
              <FunctionPort key={`in-${inp.component}-${inp.order}`} side='left'>
                <Handle
                  type='target'
                  position={Position.Left}
                  id={`fin-${inp.component}`}
                />
                <FunctionPortLabel>{inp.name}</FunctionPortLabel>
              </FunctionPort>
            ))}
          </FunctionPortsRow>
        </>
      )}
      {data.outputs.length > 0 && (
        <>
          <FunctionPortGroupLabel>Outputs</FunctionPortGroupLabel>
          <FunctionPortsRow>
            {data.outputs.map((out) => (
              <FunctionPort key={`out-${out.component}-${out.order}`} side='right'>
                <FunctionPortLabel>{out.name}</FunctionPortLabel>
                <Handle
                  type='source'
                  position={Position.Right}
                  id={`fout-${out.component}`}
                />
              </FunctionPort>
            ))}
          </FunctionPortsRow>
        </>
      )}
    </FunctionNodeBox>
  )
})
FunctionNode.displayName = 'FunctionNode'
